import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { CustomSelect } from '@/components/ui/select';
import { apiErrorMessage } from '@shared/api/client';
import { listSubCategoriesByCategory } from '@shared/api/services';
import * as admin from '@shared/api/admin';
import {
  DEFAULT_SUB_CATEGORIES,
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  type CreateServicePayload,
  type QuestionnaireQuestion,
  type ServiceCategory,
  type SubCategoriesByCategory,
  type Uuid,
} from '@shared/types/api';
import {
  useAdminAuthorities,
  useAdminServices,
  useCreateService,
  useUpdateService,
} from '@/hooks/use-admin';
import { L } from './field-label';
import { FeeBreakdownModal } from './fee-breakdown-modal';
import { PricingFeeSections } from './pricing-fee-sections';
import { QuestionnaireBuilder } from './questionnaire-builder';
import { RequiredDocumentsSection } from './required-documents-section';
import type { AdditionalCharge, ServiceEditorDialogProps, ServiceFormValues } from './types';
import { cleanHsn, cleanPercentage, cleanRupees, extractS3Key } from './utils';

export function ServiceEditorDialog({
  existing,
  existingServices = [],
  readOnly = false,
  onClose,
}: ServiceEditorDialogProps) {
  const create = useCreateService();
  const update = useUpdateService();
  const isEdit = !!existing;

  const { data: allServices = [] } = useAdminServices(true);
  const catalogServices = existingServices.length > 0 ? existingServices : allServices;

  const { data: authorities = [] } = useAdminAuthorities();
  const [selectedAuthorityId, setSelectedAuthorityId] = useState<string>('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<ServiceFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: existing?.name ?? '',
      category: existing?.category ?? SERVICE_CATEGORIES[0],
      authorityId: existing?.authority?.id ?? (existing as any)?.authorityId ?? undefined,
      authorityServiceCode: existing?.authorityServiceCode ?? undefined,
      subCategory: existing?.subCategory ?? '',
      description: existing?.description ?? '',
      timelineText: existing?.timelineText ?? '',
      defaultApplicationFeeRupees: existing?.defaultAssociateFeeRupees ?? existing?.defaultApplicationFeeRupees ?? '0',
      defaultServiceChargeRupees: existing?.defaultPlatformFeeRupees ?? existing?.defaultServiceChargeRupees ?? '0',
      associateGstPercentage: (existing as any)?.associateGstPercentage ?? existing?.gstPercentage ?? '18.00',
      associateHsnSacCode: (existing as any)?.associateHsnSacCode ?? existing?.hsnSacCode ?? '998213',
      platformGstPercentage: (existing as any)?.platformGstPercentage ?? existing?.gstPercentage ?? '18.00',
      platformHsnSacCode: (existing as any)?.platformHsnSacCode ?? existing?.hsnSacCode ?? '998213',
      defaultAssociateSplitPercentage: (existing as any)?.defaultAssociateSplitPercentage ?? '60.00',
      governmentFeeRupees: (existing as any)?.governmentFeeRupees ?? '0',
      isStartsFrom: existing?.isStartsFrom ?? false,
      startsFromRupees: existing?.startsFromRupees ?? '0',
      questionnaire: existing?.questionnaire ?? [],
      images: existing?.images ?? [],
      requiredDocuments: existing?.requiredDocuments ?? [],
    },
  });

  // Watch form values
  const nameValue = watch('name');
  const categoryValue = watch('category');
  const subCategoryValue = watch('subCategory');
  const imagesValue = watch('images');
  const requiredDocumentsValue = watch('requiredDocuments') ?? [];
  const questionnaireValue = watch('questionnaire') ?? [];
  const defaultAppFeeValue = watch('defaultApplicationFeeRupees');
  const assocGstValue = watch('associateGstPercentage');
  const defaultPlatformFeeValue = watch('defaultServiceChargeRupees');
  const platformGstValue = watch('platformGstPercentage');
  const govtFeeValue = watch('governmentFeeRupees');

  useEffect(() => {
    if (!existing || authorities.length === 0) return;
    const existingAuthId = existing.authority?.id ?? (existing as any).authorityId;
    if (existingAuthId) {
      setSelectedAuthorityId(existingAuthId);
      return;
    }
    const matchedAuth = authorities.find((auth) =>
      auth.services.some(
        (s) => s.name === existing.name || `${s.code} - ${s.name}` === existing.name
      )
    );
    if (matchedAuth) {
      setSelectedAuthorityId(matchedAuth.id);
    }
  }, [existing, authorities]);

  const availableAuthorities = useMemo(
    () => authorities.filter((a) => !a.isArchived || a.id === selectedAuthorityId),
    [authorities, selectedAuthorityId]
  );

  const selectedAuthority = useMemo(
    () => authorities.find((a) => a.id === selectedAuthorityId),
    [authorities, selectedAuthorityId]
  );
  const selectedAuthorityServices = useMemo(
    () => selectedAuthority?.services.filter((s) => !s.isArchived) ?? [],
    [selectedAuthority]
  );

  const isAuthorityServiceCreated = useMemo(() => {
    return (authService: { code?: string; name: string }) => {
      const code = authService.code?.trim().toLowerCase();
      const rawName = authService.name.trim().toLowerCase();
      const prefixedName = authService.code
        ? `${authService.code} - ${authService.name}`.trim().toLowerCase()
        : rawName;

      return catalogServices.some((svc) => {
        // In edit mode, the current service is not considered a duplicate of itself
        if (existing?.id && svc.id === existing.id) return false;

        const svcName = svc.name.trim().toLowerCase();
        const svcCode = svc.authorityServiceCode?.trim().toLowerCase();
        const svcAuthId = svc.authority?.id ?? (svc as any).authorityId;

        // 1. Same authority ID & matching authority service code
        if (
          code &&
          selectedAuthorityId &&
          svcAuthId === selectedAuthorityId &&
          svcCode === code
        ) {
          return true;
        }

        // 2. Matching authority service code
        if (code && svcCode && svcCode === code) {
          return true;
        }

        // 3. Matching service name (exact raw name or code-prefixed name)
        if (svcName === rawName || svcName === prefixedName) {
          return true;
        }

        return false;
      });
    };
  }, [catalogServices, existing?.id, selectedAuthorityId]);

  function checkIsNameDuplicate(nameToCheck: string) {
    const trimmed = nameToCheck.trim().toLowerCase();
    if (!trimmed) return false;
    return catalogServices.some(
      (s) => s.name.trim().toLowerCase() === trimmed && s.id !== existing?.id
    );
  }

  function handleNameChange(newName: string, code?: string) {
    setValue('name', newName, { shouldValidate: true });
    if (code !== undefined) {
      setValue('authorityServiceCode', code);
    }
    const isDup = newName
      ? isAuthorityServiceCreated({ code, name: newName }) || checkIsNameDuplicate(newName)
      : false;
    if (isDup) {
      setError('name', { type: 'manual', message: 'A service with this name/code already exists' });
    } else {
      clearErrors('name');
    }
  }

  function handleAuthorityChange(authId: string) {
    setSelectedAuthorityId(authId);
    setValue('authorityId', authId || undefined);
    const auth = authorities.find((a) => a.id === authId);
    const activeServices = auth?.services.filter((s) => !s.isArchived) ?? [];
    if (auth && activeServices.length > 0) {
      // Pick the first available service that is not already created in catalog
      const availableService = activeServices.find((s) => !isAuthorityServiceCreated(s));
      if (availableService) {
        handleNameChange(availableService.name, availableService.code);
      } else {
        // All services for this authority are already created
        handleNameChange('', undefined);
      }
    } else {
      handleNameChange('', undefined);
    }
  }

  const subCatsQuery = useQuery<SubCategoriesByCategory>({
    queryKey: ['services', 'sub-categories'],
    queryFn: listSubCategoriesByCategory,
    staleTime: 5 * 60_000,
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const addQuestion = () =>
    setValue('questionnaire', [
      ...questionnaireValue,
      {
        id: crypto.randomUUID(),
        label: '',
        type: 'TEXT',
        options: [],
        required: true,
        order: questionnaireValue.length,
      },
    ]);

  const updateQuestion = (idx: number, patch: Partial<QuestionnaireQuestion>) =>
    setValue(
      'questionnaire',
      questionnaireValue.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    );

  const removeQuestion = (idx: number) =>
    setValue(
      'questionnaire',
      questionnaireValue
        .filter((_, i) => i !== idx)
        .map((q, i) => ({ ...q, order: i })),
    );

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= questionnaireValue.length) return;
    const copy = [...questionnaireValue];
    [copy[idx], copy[swap]] = [copy[swap], copy[idx]];
    setValue('questionnaire', copy.map((q, i) => ({ ...q, order: i })));
  };

  const [customMode, setCustomMode] = useState(false);
  const [showOverviewPopup, setShowOverviewPopup] = useState(false);

  const [showCustomDocInput, setShowCustomDocInput] = useState(false);
  const [customDocName, setCustomDocName] = useState('');
  const [customDocs, setCustomDocs] = useState<string[]>([]);
  const [additionalCharges, setAdditionalCharges] = useState<AdditionalCharge[]>([]);
  const presets = useMemo(() => ['Aadhar', 'PAN Card', 'Licence', 'Passport'], []);
  const allDocsList = useMemo(() => {
    const union = new Set([...presets, ...requiredDocumentsValue, ...customDocs]);
    return Array.from(union);
  }, [requiredDocumentsValue, customDocs, presets]);

  const handleAddCustomDoc = () => {
    const name = customDocName.trim();
    if (!name) return;
    if (!allDocsList.includes(name)) {
      setCustomDocs((prev) => [...prev, name]);
    }
    if (!requiredDocumentsValue.includes(name)) {
      setValue('requiredDocuments', [...requiredDocumentsValue, name], { shouldValidate: true });
    }
    setCustomDocName('');
    setShowCustomDocInput(false);
  };

  const handleToggleDoc = (docName: string) => {
    if (requiredDocumentsValue.includes(docName)) {
      setValue('requiredDocuments', requiredDocumentsValue.filter((d) => d !== docName), { shouldValidate: true });
    } else {
      setValue('requiredDocuments', [...requiredDocumentsValue, docName], { shouldValidate: true });
    }
    clearErrors('requiredDocuments');
  };

  useEffect(() => {
    if (!existing) return;
    reset({
      name: existing.name,
      category: existing.category,
      authorityId: existing.authority?.id ?? (existing as any).authorityId ?? undefined,
      authorityServiceCode: existing.authorityServiceCode ?? undefined,
      subCategory: existing.subCategory ?? '',
      description: existing.description ?? '',
      timelineText: existing.timelineText ?? '',
      defaultApplicationFeeRupees: existing.defaultAssociateFeeRupees ?? existing.defaultApplicationFeeRupees ?? '0',
      defaultServiceChargeRupees: existing.defaultPlatformFeeRupees ?? existing.defaultServiceChargeRupees ?? '0',
      governmentFeeRupees: (existing as any).governmentFeeRupees ?? '0',
      associateGstPercentage: (existing as any).associateGstPercentage ?? existing.gstPercentage ?? '18.00',
      associateHsnSacCode: (existing as any).associateHsnSacCode ?? existing.hsnSacCode ?? '998213',
      platformGstPercentage: (existing as any).platformGstPercentage ?? existing.gstPercentage ?? '18.00',
      platformHsnSacCode: (existing as any).platformHsnSacCode ?? existing.hsnSacCode ?? '998213',
      defaultAssociateSplitPercentage: (existing as any).defaultAssociateSplitPercentage ?? '60.00',
      isStartsFrom: existing.isStartsFrom ?? false,
      startsFromRupees: existing.startsFromRupees ?? '0',
      questionnaire: existing.questionnaire ?? [],
      images: existing.images ?? [],
      requiredDocuments: existing.requiredDocuments ?? [],
    });

    if (existing.images && existing.images[0]) {
      setPreviewUrl(existing.images[0]);
    } else {
      setPreviewUrl(null);
    }
    const addlFromProp = (existing as any)?.additionalCharges;
    const policy = existing.stageRefundPolicy as Record<string, unknown> | undefined;
    const rawAddl = Array.isArray(addlFromProp)
      ? addlFromProp
      : policy && Array.isArray(policy.additionalCharges)
        ? policy.additionalCharges
        : [];
    setAdditionalCharges(
      rawAddl.map((ac: any) => ({
        id: ac.id || crypto.randomUUID(),
        name: ac.name || '',
        amountRupees: ac.amountRupees || '',
        gstPercentage: ac.gstPercentage || '18.00',
        hsnSacCode: ac.hsnSacCode || '998213',
      }))
    );
  }, [existing, reset]);

  function setRupees(k: 'defaultApplicationFeeRupees' | 'defaultServiceChargeRupees' | 'governmentFeeRupees', raw: string) {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    const head = parts[0] ?? '';
    const tail = parts.length > 1 ? '.' + parts.slice(1).join('').slice(0, 2) : '';
    setValue(k, head + tail);
  }

  function changeCategory(next: ServiceCategory) {
    setValue('category', next);
    setCustomMode(false);
    const options = subCatsQuery.data?.byCategory[next] ?? DEFAULT_SUB_CATEGORIES[next];
    const current = (subCategoryValue ?? '').trim();
    if (current && !options.includes(current)) setValue('subCategory', '');
  }

  const subCategoryOptions = useMemo(() => {
    const fromServer = subCatsQuery.data?.byCategory[categoryValue];
    return fromServer && fromServer.length > 0 ? fromServer : DEFAULT_SUB_CATEGORIES[categoryValue];
  }, [subCatsQuery.data, categoryValue]);

  useEffect(() => {
    const sc = (subCategoryValue ?? '').trim();
    if (sc.length > 0 && !subCategoryOptions.includes(sc)) setCustomMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryOptions.join('|')]);

  const onSubmit = (formValues: ServiceFormValues) => {
    let hasError = false;
    const nameTrimmed = formValues.name.trim();

    if (!nameTrimmed) {
      setError('name', { type: 'manual', message: 'Service name is required' });
      hasError = true;
    } else if (
      existingServices.some(
        (s) => s.name.trim().toLowerCase() === nameTrimmed.toLowerCase() && s.id !== existing?.id
      )
    ) {
      setError('name', { type: 'manual', message: 'A service with this name already exists' });
      hasError = true;
    }

    if (!formValues.images || formValues.images.length === 0) {
      setError('images', { type: 'manual', message: 'Please upload a service image' });
      hasError = true;
    }

    if (!formValues.requiredDocuments || formValues.requiredDocuments.length === 0) {
      setError('requiredDocuments', { type: 'manual', message: 'Please select at least one required document' });
      hasError = true;
    }

    if (hasError) return;

    const cleanedQuestionnaire = (formValues.questionnaire ?? [])
      .map((q, i) => ({
        ...q,
        label: q.label.trim(),
        options: q.options.map((o) => o.trim()).filter((o) => o.length > 0),
        order: i,
      }))
      .filter((q) => q.label.length > 0);

    for (const q of cleanedQuestionnaire) {
      if (q.type === 'OPTIONS' && q.options.length === 0) {
        window.alert(`Question "${q.label}" is OPTIONS but has no options.`);
        return;
      }
    }
    const cleanedImages = (formValues.images ?? []).map(extractS3Key);
    const cleanedAddlCharges = additionalCharges
      .map((ac) => ({
        id: ac.id || crypto.randomUUID(),
        name: ac.name.trim(),
        amountRupees: cleanRupees(ac.amountRupees, '0'),
        gstPercentage: cleanPercentage(ac.gstPercentage, '18.00'),
        hsnSacCode: cleanHsn(ac.hsnSacCode, '998213'),
      }))
      .filter((ac) => ac.name.length > 0);

    const existingPolicy =
      existing?.stageRefundPolicy && typeof existing.stageRefundPolicy === 'object'
        ? (existing.stageRefundPolicy as Record<string, unknown>)
        : {};

    const assocHsn = cleanHsn(formValues.associateHsnSacCode, '998213');
    const platformHsn = cleanHsn(formValues.platformHsnSacCode, '998213');
    const assocGst = cleanPercentage(formValues.associateGstPercentage, '18.00');
    const platformGst = cleanPercentage(formValues.platformGstPercentage, '18.00');

    const payload: CreateServicePayload = {
      name: nameTrimmed,
      category: formValues.category,
      authorityId: selectedAuthorityId || formValues.authorityId || undefined,
      authorityServiceCode: formValues.authorityServiceCode || undefined,
      subCategory: formValues.subCategory?.trim() || undefined,
      description: formValues.description?.trim() || undefined,
      timelineText: formValues.timelineText?.trim() || undefined,
      defaultApplicationFeeRupees: cleanRupees(formValues.defaultApplicationFeeRupees, '0'),
      defaultServiceChargeRupees: cleanRupees(formValues.defaultServiceChargeRupees, '0'),
      governmentFeeRupees: cleanRupees(formValues.governmentFeeRupees, '0'),
      associateGstPercentage: assocGst,
      associateHsnSacCode: assocHsn,
      platformGstPercentage: platformGst,
      platformHsnSacCode: platformHsn,
      gstPercentage: assocGst,
      hsnSacCode: assocHsn,
      isStartsFrom: formValues.isStartsFrom ?? false,
      startsFromRupees: formValues.isStartsFrom ? cleanRupees(formValues.startsFromRupees, '0') : '0',
      additionalCharges: cleanedAddlCharges,
      stageRefundPolicy: {
        ...existingPolicy,
        additionalCharges: cleanedAddlCharges,
      },
      questionnaire: cleanedQuestionnaire,
      images: cleanedImages,
      requiredDocuments: formValues.requiredDocuments ?? [],
    };

    if (isEdit && existing) {
      update.mutate({ id: existing.id as Uuid, payload }, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  const m = isEdit ? update : create;
  const CUSTOM_SENTINEL = '__CUSTOM__';
  const selectValue = customMode ? CUSTOM_SENTINEL : subCategoryValue ?? '';

  // Calculate Associate Fee totals
  const assocFeeNum = parseFloat(defaultAppFeeValue || '0') || 0;
  const assocGstNum = parseFloat(assocGstValue || '0') || 0;
  const assocGstAmountNum = assocFeeNum * (assocGstNum / 100);
  const totalAssocFee = (assocFeeNum + assocGstAmountNum).toFixed(2);

  // Calculate Platform Fee totals
  const platformFeeNum = parseFloat(defaultPlatformFeeValue || '0') || 0;
  const platformGstNum = parseFloat(platformGstValue || '0') || 0;
  const platformGstAmountNum = platformFeeNum * (platformGstNum / 100);
  const totalPlatformFee = (platformFeeNum + platformGstAmountNum).toFixed(2);

  // Calculate Additional Charges totals
  let additionalChargesSubtotal = 0;
  let additionalChargesGstTotal = 0;
  const computedAddlCharges = additionalCharges.map((chg) => {
    const amt = parseFloat(chg.amountRupees || '0') || 0;
    const gstPct = parseFloat(chg.gstPercentage || '0') || 0;
    const gstAmt = amt * (gstPct / 100);
    const totalAmt = amt + gstAmt;
    additionalChargesSubtotal += amt;
    additionalChargesGstTotal += gstAmt;
    return {
      ...chg,
      gstAmount: gstAmt.toFixed(2),
      totalAmount: totalAmt.toFixed(2),
    };
  });

  // Calculate Government Fee
  const govtFeeNum = parseFloat(govtFeeValue || '0') || 0;

  const grandTotalSubtotal = assocFeeNum + platformFeeNum + additionalChargesSubtotal + govtFeeNum;
  const grandTotalGst = assocGstAmountNum + platformGstAmountNum + additionalChargesGstTotal;
  const grandTotalAmount = grandTotalSubtotal + grandTotalGst;

  return (
    <Dialog
      open
      onClose={onClose}
      title={readOnly ? `View ${existing?.name || 'Service'}` : isEdit ? `Edit ${existing?.name}` : 'New service'}
      className="max-w-3xl"
      footer={
        readOnly ? (
          <Button variant="ghost" onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={m.isPending}>
              {m.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
            </Button>
          </>
        )
      }
    >
      <form onSubmit={readOnly ? (e) => { e.preventDefault(); onClose(); } : handleSubmit(onSubmit)} className="space-y-4 text-sm">
        <fieldset disabled={readOnly} className="space-y-4 text-sm">
        {m.isError ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-700">
            {apiErrorMessage(m.error)}
          </div>
        ) : null}

        {/* Row 1: Authority & Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <L label="Authority">
            <CustomSelect
              value={selectedAuthorityId}
              onChange={handleAuthorityChange}
              placeholder="— Select Authority —"
              searchable
              searchPlaceholder="Search authority…"
              options={[
                { value: '', label: '— Select Authority —' },
                ...availableAuthorities.map((auth) => ({
                  value: auth.id,
                  label: auth.name,
                })),
              ]}
            />
          </L>
          <L label="Name *">
            {selectedAuthorityServices.length > 0 ? (
              <CustomSelect
                value={nameValue}
                onChange={(val) => {
                  const matchedS = selectedAuthorityServices.find((s) => s.name === val);
                  handleNameChange(val, matchedS?.code || undefined);
                }}
                placeholder="— Select Service —"
                error={!!errors.name}
                searchable
                searchPlaceholder="Search service…"
                options={[
                  { value: '', label: '— Select Service —' },
                  ...selectedAuthorityServices.map((s) => {
                    const isAlreadyCreated = isAuthorityServiceCreated(s);
                    return {
                      value: s.name,
                      label: s.code ? `${s.code} - ${s.name}` : s.name,
                      disabled: isAlreadyCreated,
                      disabledReason: 'Already created',
                    };
                  }),
                  ...(nameValue && !selectedAuthorityServices.some((s) => s.name === nameValue)
                    ? [{ value: nameValue, label: nameValue }]
                    : []),
                ]}
              />
            ) : (
              <Input
                {...register('name', { required: 'Service name is required' })}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter service name"
                className={cn(errors.name && 'border-red-500 focus:border-red-600 focus:ring-red-500/30')}
              />
            )}
            {errors.name ? <p className="mt-1 text-xs font-medium text-red-600">{errors.name.message}</p> : null}
          </L>
        </div>

        {/* Row 2: Category & Sub-category */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <L label="Category *">
            <CustomSelect
              value={categoryValue}
              onChange={(val) => changeCategory(val as ServiceCategory)}
              options={SERVICE_CATEGORIES.map((cat) => ({
                value: cat,
                label: SERVICE_CATEGORY_LABELS[cat],
              }))}
            />
          </L>
          <L label="Sub-category">
            <CustomSelect
              value={selectValue}
              onChange={(v) => {
                if (v === CUSTOM_SENTINEL) {
                  setCustomMode(true);
                  setValue('subCategory', '');
                } else {
                  setCustomMode(false);
                  setValue('subCategory', v);
                }
              }}
              placeholder="— Select sub-category —"
              options={[
                { value: '', label: '— Select sub-category —' },
                ...subCategoryOptions.map((opt) => ({ value: opt, label: opt })),
                { value: CUSTOM_SENTINEL, label: '+ Add custom…' },
              ]}
            />
            {customMode ? (
              <Input
                {...register('subCategory')}
                placeholder="Type a new sub-category (e.g. Mortgage)"
                className="mt-2"
                autoFocus
              />
            ) : null}
          </L>
        </div>

        {/* Service Image */}
        <L label="Service Image *">
          <div className="mt-1 flex items-center gap-3">
            {previewUrl || (imagesValue && imagesValue[0]) ? (
              <div className="relative h-16 w-24 border border-gray-200 rounded-md overflow-hidden bg-gray-50 shrink-0">
                <img
                  src={previewUrl || imagesValue?.[0]}
                  alt="Preview"
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setValue('images', []);
                    setPreviewUrl(null);
                  }}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 text-white font-bold opacity-0 hover:opacity-100 transition-opacity"
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="flex h-16 w-24 items-center justify-center border border-dashed border-gray-300 rounded-md bg-gray-50 text-[10px] text-gray-400 shrink-0">
                No Image
              </div>
            )}

            <div className="flex-1">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  setUploadError(null);
                  try {
                    const key = await admin.uploadServiceImage(file);
                    setValue('images', [key]);
                    setPreviewUrl(URL.createObjectURL(file));
                    clearErrors('images');
                  } catch (err: any) {
                    setUploadError(err.message || 'Upload failed');
                  } finally {
                    setUploading(false);
                  }
                }}
                className="block w-full text-xs text-gray-500 file:mr-3 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 file:cursor-pointer hover:file:bg-brand-100"
              />
              <p className="mt-1 text-[10px] text-gray-400">
                JPG, PNG, WEBP, or SVG. Max 15MB.
              </p>
              {uploading && <p className="text-[11px] text-brand-600 font-medium">Uploading to S3...</p>}
              {uploadError && <p className="text-[11px] text-red-600 font-medium">{uploadError}</p>}
              {errors.images && <p className="text-[11px] text-red-600 font-medium">{errors.images.message}</p>}
            </div>
          </div>
        </L>

        <L label="Description">
          <textarea
            {...register('description')}
            rows={2}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </L>

        <L label="Timeline">
          <Input
            {...register('timelineText')}
            placeholder="e.g. 2-3 days"
          />
        </L>

        {/* Pricing & Fee Sections */}
        <PricingFeeSections
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          setRupees={setRupees}
          assocGstAmountNum={assocGstAmountNum}
          totalAssocFee={totalAssocFee}
          platformGstAmountNum={platformGstAmountNum}
          totalPlatformFee={totalPlatformFee}
          additionalCharges={additionalCharges}
          setAdditionalCharges={setAdditionalCharges}
          computedAddlCharges={computedAddlCharges}
          grandTotalSubtotal={grandTotalSubtotal}
          grandTotalGst={grandTotalGst}
          grandTotalAmount={grandTotalAmount}
          onShowOverviewPopup={() => setShowOverviewPopup(true)}
        />

        {/* Fee Breakdown Overview Modal */}
        <FeeBreakdownModal
          open={showOverviewPopup}
          onClose={() => setShowOverviewPopup(false)}
          assocFeeNum={assocFeeNum}
          assocGstNum={assocGstNum}
          assocGstAmountNum={assocGstAmountNum}
          totalAssocFee={totalAssocFee}
          platformFeeNum={platformFeeNum}
          platformGstNum={platformGstNum}
          platformGstAmountNum={platformGstAmountNum}
          totalPlatformFee={totalPlatformFee}
          govtFeeNum={govtFeeNum}
          computedAddlCharges={computedAddlCharges}
          grandTotalSubtotal={grandTotalSubtotal}
          grandTotalGst={grandTotalGst}
          grandTotalAmount={grandTotalAmount}
        />

        {/* Required Documents Section */}
        <RequiredDocumentsSection
          allDocsList={allDocsList}
          requiredDocuments={requiredDocumentsValue}
          onToggleDoc={handleToggleDoc}
          showCustomDocInput={showCustomDocInput}
          setShowCustomDocInput={setShowCustomDocInput}
          customDocName={customDocName}
          setCustomDocName={setCustomDocName}
          onAddCustomDoc={handleAddCustomDoc}
          error={errors.requiredDocuments?.message}
        />

        {/* Pre-questionnaire Section */}
        <QuestionnaireBuilder
          questions={questionnaireValue}
          onAddQuestion={addQuestion}
          onUpdateQuestion={updateQuestion}
          onRemoveQuestion={removeQuestion}
          onMoveQuestion={moveQuestion}
        />
        </fieldset>
      </form>
    </Dialog>
  );
}
